import {
  Button,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconKey } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/auth";
import { PasswordModal } from "../../auth/password-modal";
import { LOCALE_LABEL, LOCALES, setLocale, type UiLocale } from "../../i18n";

/** Personal settings: language, colour scheme, password. Stored per browser (theme, language). */
export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const [passwordOpen, passwordModal] = useDisclosure(false);

  return (
    <Stack gap="md" maw={640}>
      <Title order={2}>{t("settings.title")}</Title>
      <Paper withBorder p="md">
        <Stack gap="sm">
          <div>
            <Text fw={600}>{t("settings.theme")}</Text>
            <Text size="sm" c="dimmed">
              {t("settings.themeHint")}
            </Text>
          </div>
          <SegmentedControl
            value={colorScheme}
            onChange={(v) => setColorScheme(v as "light" | "dark" | "auto")}
            data={[
              { value: "light", label: t("settings.themes.light") },
              { value: "dark", label: t("settings.themes.dark") },
              { value: "auto", label: t("settings.themes.auto") },
            ]}
          />
        </Stack>
      </Paper>
      <Paper withBorder p="md">
        <Stack gap="sm">
          <div>
            <Text fw={600}>{t("nav.language")}</Text>
            <Text size="sm" c="dimmed">
              {t("settings.languageHint")}
            </Text>
          </div>
          <SegmentedControl
            value={(i18n.language as UiLocale) ?? "hy"}
            onChange={(v) => setLocale(v as UiLocale)}
            data={LOCALES.map((l) => ({ value: l, label: LOCALE_LABEL[l] }))}
          />
        </Stack>
      </Paper>
      <Paper withBorder p="md">
        <Stack gap="sm">
          <div>
            <Text fw={600}>{t("settings.account")}</Text>
            <Text size="sm" c="dimmed">
              {user?.username} · {user ? t(`auth.role.${user.role}`) : ""}
            </Text>
          </div>
          <Group>
            <Button
              variant="default"
              leftSection={<IconKey size={16} />}
              onClick={passwordModal.open}
            >
              {t("password.menu")}
            </Button>
          </Group>
        </Stack>
      </Paper>
      <PasswordModal opened={passwordOpen} onClose={passwordModal.close} />
    </Stack>
  );
}
